import { InstagramAudioListStats, getInstagramAudioListStats, GetInstagramAudioListStatsParams } from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../helpers';

type Params = GetInstagramAudioListStatsParams;

export function useInstagramAudioListStats({
    page,
    page_size,
    ordering,
    author,
    title,
    instagram_id,
    min_reels_count,
    max_reels_count,
}: Params) {
    const [audioStats, setAudioStats] = useState<InstagramAudioListStats[]>([]);
    const [count, setCount] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasError, setHasError] = useState(false);

    const fetchAudioStats = async (params: Params, requestInit: RequestInit) => {
        try {
            const response = await getInstagramAudioListStats(params, requestInit);
            if (response.status === 404) {
                setHasError(true);
                showNotification(response.data.detail, 'error');
                return;
            }

            setAudioStats(response.data.results);
            setCount(response.data.count);
            setIsInitialized(true);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            setHasError(true);
            showNotification(`Could not fetch instagram audio stats`, 'error');
        }
    };

    const init = useCallback(
        async (requestInit: RequestInit) => {
            await fetchAudioStats(
                { page, page_size, ordering, author, title, instagram_id, min_reels_count, max_reels_count },
                requestInit
            );
        },
        [page, page_size, ordering, author, title, instagram_id, min_reels_count, max_reels_count]
    );

    const reset = () => {
        setAudioStats([]);
        setIsInitialized(false);
        setHasError(false);
    };

    return {
        audioStats,
        count,
        isInitialized,
        isLoading: !isInitialized && !hasError,
        hasError,

        init,
        reset,
    };
}
